import { createAsyncThunk, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import { AxiosError } from "axios";
import DocumentQuerySchema, { DocumentQueryJSON } from "documents/_api/commands/_schema/document-query-schema";
import DocumentAPIService from "documents/_api/document-api-service";
import DocumentQuery from "documents/_api/queries/document-query";
import { DocumentJSON } from "documents/_schema/document-schema";
import Document from "documents/document";
import UnavailableDocument from "documents/types/unavailable-documents/unavailable-document";
import { UnavailableDocumentJSON } from "documents/types/unavailable-documents/unavailable-document-schema";
import _ from "lodash";
import Session from "users/__session/session";

export type AnyDocumentJSON = DocumentJSON | UnavailableDocumentJSON;

const isUnavailableDocumentJSON = (value: AnyDocumentJSON): value is UnavailableDocumentJSON => {
  return "issue" in value;
};

export type DocumentStoreState = {
  byQuery: {
    entries: Record<string, PaginatedResponse<AnyDocumentJSON>>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  byId: {
    entries: Record<string, AnyDocumentJSON>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  interfaceQueries: {
    entries: Record<string, DocumentQueryJSON>;
  };
};

const initialState: DocumentStoreState = {
  byQuery: {
    entries: {},
    loading: {},
    error: {},
  },
  byId: {
    entries: {},
    loading: {},
    error: {},
  },
  interfaceQueries: {
    entries: {},
  },
};

export const populateDocumentsByQuery = createAsyncThunk<
  PaginatedResponse<Document>,
  { session: Session; query: DocumentQuery }
>(
  "documents/getDocumentsByQuery",
  async ({ session, query }: { session: Session; query: DocumentQuery }, thunkAPI) => {
    const apiService = new DocumentAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.getDocumentsInfo(query, abortController);
    } catch (error) {
      const apiError = error as AxiosError;
      return thunkAPI.rejectWithValue(apiError?.response?.data ?? error?.toString() ?? "An error occurred.");
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  },
  {
    condition: ({ query }: { session: Session; query: DocumentQuery }, { getState }) => {
      const state = getState() as RootState;
      const queryKey = query.cacheKey;
      // Only dispatch if not already loading
      return !state.notifications.byQuery.loading[queryKey];
    },
  }
);

export const populateDocuments = createAsyncThunk<
  (Document | UnavailableDocument)[],
  { session: Session; documentIds: Guid[] }
>(
  "documents/getDocumentsByIds",
  async ({ session, documentIds }: { session: Session; documentIds: Guid[] }, thunkAPI) => {
    if (!documentIds.length) return [];

    const store: { documents: DocumentStoreState } = thunkAPI.getState() as { documents: DocumentStoreState };
    const missingDocumentsIds = _.uniqWith(
      documentIds.filter((documentId) => !store.documents.byId.entries[documentId.value]),
      (a, b) => a.isEqualTo(b)
    );
    if (!missingDocumentsIds.length) return [];

    const apiService = new DocumentAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.getDocumentsInfoByIds(documentIds, abortController);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);
export const documentsSlice = createSlice({
  name: "documents",
  initialState,
  reducers: {
    addDocument: (state, action: PayloadAction<Document | UnavailableDocument>) => {
      const id: Guid = action.payload.id;
      if (!id) return;

      state.byId.entries[id.value] = action.payload.toJSON();

      // Invalidate query cache
      state.byQuery = {
        entries: {},
        loading: {},
        error: {},
      };
    },
    removeDocument: (state, action: PayloadAction<Document | UnavailableDocument | Guid>) => {
      let documentId: Guid | undefined;
      if (action.payload instanceof Guid) {
        documentId = action.payload;
      } else {
        documentId = action.payload.id;
      }
      if (!documentId) {
        throw new Error("Document id not found");
      }
      delete state.byId.entries[documentId.value];
      state.byQuery = {
        entries: {},
        loading: {},
        error: {},
      };
    },
    updateDocumentsInterfaceQuery: (
      state,
      action: PayloadAction<{
        interfaceName: string;
        query: DocumentQuery;
      }>
    ) => {
      const { interfaceName, query } = action.payload;

      state.interfaceQueries.entries[interfaceName] = DocumentQuerySchema.serialize(query);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(populateDocuments.pending, (state, action) => {
      const documentIds = action.meta.arg.documentIds;
      if (!documentIds.length) return;

      for (const id of documentIds) {
        state.byId.loading[id.value] = true;
        state.byId.error[id.value] = null;
      }
    });
    builder.addCase(populateDocuments.fulfilled, (state, action) => {
      const documentIds = action.meta.arg.documentIds;
      if (!documentIds.length) return;

      for (const doc of action.payload) {
        state.byId.entries[doc.id.value] = doc.toJSON();
      }

      for (const id of documentIds) {
        state.byId.loading[id.value] = false;
        if (!state.byId.entries[id.value]) {
          state.byId.error[id.value] = "Document not found";
        }
      }
    });

    builder.addCase(populateDocuments.rejected, (state, action) => {
      const documentIds = action.meta.arg.documentIds;
      if (!documentIds.length) {
        return;
      }
      for (const id of documentIds) {
        state.byId.loading[id.value] = false;
        state.byId.error[id.value] = "Error fetching document info.";
      }
    });

    builder.addCase(populateDocumentsByQuery.pending, (state, action) => {
      const query = action.meta.arg.query.cacheKey;
      state.byQuery.loading[query] = true;
      state.byQuery.error[query] = null;
    });
    builder.addCase(populateDocumentsByQuery.fulfilled, (state, action) => {
      if (!action.meta.arg.query?.filterParameters) return;

      const queryKey = action.meta.arg.query.cacheKey;
      state.byQuery.loading[queryKey] = false;

      const page = action.payload;

      const jsonData = page.data.map(
        (document: Document | UnavailableDocument) => document.toJSON() as AnyDocumentJSON
      );

      const jsonPage = PaginatedResponse.from<AnyDocumentJSON>(jsonData, page.headers);

      state.byQuery.entries[queryKey] = jsonPage;
      state.byQuery.error[queryKey] = null;
    });
    builder.addCase(populateDocumentsByQuery.rejected, (state, action) => {
      const query = action.meta.arg.query.cacheKey;
      state.byQuery.loading[query] = false;
      state.byQuery.error[query] = action.payload as string;
    });
  },
});

export const { addDocument, removeDocument, updateDocumentsInterfaceQuery } = documentsSlice.actions;

const selectDocumentsByQuery = createSelector(
  [(state: RootState) => state.documents.byQuery.entries, (_: RootState, query?: DocumentQuery) => query?.cacheKey],
  (entries, queryKey) => {
    if (!queryKey) return;
    const pagedJson = entries[queryKey];
    if (!pagedJson) return;
    const docData = pagedJson.data.map((docJson: AnyDocumentJSON) =>
      isUnavailableDocumentJSON(docJson) ? UnavailableDocument.fromJSON(docJson) : Document.fromJSON(docJson)
    );
    return PaginatedResponse.from<Document | UnavailableDocument>(docData, pagedJson.headers);
  }
);
export const getDocumentsByQuery = (query?: DocumentQuery) => (state: RootState) =>
  selectDocumentsByQuery(state, query);

export const getIsLoadingDocumentsByQuery = (query?: DocumentQuery) => (state: RootState) => {
  if (!query) return false;
  return state.documents.byQuery.loading[query.cacheKey] ?? false;
};

export const getErrorLoadingDocumentsByQuery = (query?: DocumentQuery) => (state: RootState) => {
  if (!query) return false;
  return state.documents.byQuery.error[query.cacheKey] ?? false;
};

const selectDocumentById = createSelector(
  [(state: RootState) => state.documents.byId.entries, (_: RootState, id?: Guid) => id?.value],
  (entries, id) => {
    if (!id) return;
    const docJson = entries[id];
    if (!docJson) return;
    return isUnavailableDocumentJSON(docJson) ? UnavailableDocument.fromJSON(docJson) : Document.fromJSON(docJson);
  }
);
export const getDocumentById = (id?: Guid) => (state: RootState) => selectDocumentById(state, id);

export const getDocumentIsLoading = (id?: Guid) => (state: RootState) => {
  if (!id) return false;
  return state.documents.byId.loading[id.value];
};

export const selectDocumentsByIds = createSelector(
  [(state: RootState) => state.documents.byId.entries, (_: RootState, documentIds: Guid[]) => documentIds],
  (entries, documentIds) => {
    const docsJson = documentIds.map((documentId) => entries[documentId.value]).filter(Boolean);
    return docsJson.map((docJson: AnyDocumentJSON) =>
      isUnavailableDocumentJSON(docJson) ? UnavailableDocument.fromJSON(docJson) : Document.fromJSON(docJson)
    );
  }
);
export const getDocumentsByIds = (documentIds: Guid[]) => (state: RootState) =>
  selectDocumentsByIds(state, documentIds);

const selectLoadingDocumentIds = createSelector([(state: RootState) => state.documents.byId.entries], (entries) =>
  Object.keys(entries)
    .filter((documentId) => entries[documentId])
    .map((documentId) => Guid.fromJSON(documentId) as Guid)
);
export const getLoadingDocumentIds = (state: RootState) => selectLoadingDocumentIds(state);

export const getDocumentError = (id?: Guid) => (state: RootState) => {
  if (!id) return;
  return state.documents.byId.error[id.value];
};
export type DocumentError = {
  id: Guid;
  error: string;
};

export const selectAllDocumentErrors = createSelector(
  [(state: RootState) => state.documents.byId.error],
  (errors): DocumentError[] =>
    Object.keys(errors)
      .filter((documentId) => documentId && errors[documentId])
      .map((documentId) => {
        const id = Guid.fromJSON(documentId);
        const error = errors[documentId];
        if (!id || !error) return null;
        return { id, error } as DocumentError;
      })
      .filter((item): item is DocumentError => item !== null)
);
export const getAllDocumentErrors = (state: RootState) => selectAllDocumentErrors(state);

const selectAreErrorsLoadingDocumentsByIds = createSelector(
  [(state: RootState) => state.documents.byId.error, (_: RootState, documentIds: Guid[]) => documentIds],
  (errors, documentIds) => {
    return documentIds.some((documentId) => errors[documentId.value]);
  }
);
export const getAreErrorsLoadingDocumentsByIds = (documentIds: Guid[]) => (state: RootState) =>
  selectAreErrorsLoadingDocumentsByIds(state, documentIds);

const selectDocumentsQueryByInterface = createSelector(
  [(state: RootState) => state.documents.interfaceQueries.entries, (_: RootState, interfaceName: string) => interfaceName],
  (queries, interfaceName) => {
    const query = queries[interfaceName];
    if (!query) return;
    return DocumentQuerySchema.deserialize(query);
  }
);
export const getDocumentsQueryByInterface = (interfaceName: string) => (state: RootState) =>
  selectDocumentsQueryByInterface(state, interfaceName);
